// docs/56: the board as it stands part-way through a pulse's playback. It begins as the board the
// pulse began on; each entry is applied as its clip lands (where units stand, their HP, who is
// down, who was called in). What the pulse changed on the ground — a door or gate opened, a lever
// thrown, a plate pressed, a brazier lit, terrain set — the log does not spell out square by
// square, so it is taken from the board after the pulse at the entry that did it.
import type { FogView, MatterPatch, ResolutionEntry, Snapshot, ThingView, Tile, UnitState } from '../sim/types';

/** What one batch of entries changed: the units shown, the party's eyes, the ground. */
export interface LiveChange { units: boolean; sight: boolean; ground: boolean }

const key = (t: Tile) => `${t.x},${t.y}`;
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** docs/114: `set`'s patches over `was` (an empty patch clears its square), in board order. */
export function mergeMatter(was: readonly MatterPatch[] | undefined, set: readonly MatterPatch[]): MatterPatch[] {
  const by = new Map((was ?? []).map((m) => [key(m.tile), m]));
  for (const m of set) {
    if (!m.surface && !m.cloud && !m.fire && !m.charge) by.delete(key(m.tile));
    else by.set(key(m.tile), m);
  }
  return [...by.values()].sort((a, b) => a.tile.x - b.tile.x || a.tile.y - b.tile.y);
}

/** docs/114: `set`'s things over `was`, by id. */
export function mergeThings(was: readonly ThingView[] | undefined, set: readonly ThingView[]): ThingView[] {
  const by = new Map((was ?? []).map((t) => [t.id, t]));
  for (const t of set) by.set(t.id, t);
  return [...by.values()].sort((a, b) => a.id - b.id);
}

export class LiveBoard {
  snapshot: Snapshot;
  /** Row-major squares taken from the board after the pulse so far (for the fog's walls and doors). */
  readonly changed = new Set<number>();
  private readonly ground = new Set<number>();          // squares that differ, not yet taken
  private readonly fixtures = new Set<number>();        // fixture ids that differ, not yet taken
  private readonly stashes = new Set<number>();
  private readonly plates = new Set<string>();          // squares of plates that will change
  private applied = -1;                                 // index into `entries` of the last applied

  constructor(start: Snapshot, private readonly end: Snapshot | undefined, private readonly entries: readonly ResolutionEntry[]) {
    this.snapshot = { ...start, units: start.units.map((u) => ({ ...u, tile: { ...u.tile } })), board: { ...start.board, tiles: [...start.board.tiles] }, ...(start.fixtures ? { fixtures: [...start.fixtures] } : {}), ...(start.stashes ? { stashes: [...start.stashes] } : {}) };
    if (!end || end.board.w !== start.board.w || end.board.h !== start.board.h) return;
    start.board.tiles.forEach((t, i) => { if (!same(t, end.board.tiles[i])) this.ground.add(i); });
    const was = new Map((start.fixtures ?? []).map((f) => [f.id, f]));
    for (const f of end.fixtures ?? []) {
      if (same(was.get(f.id), f)) continue;
      this.fixtures.add(f.id);
      if (f.kind === 'PressurePlate') this.plates.add(key(f.tile));
    }
    const had = new Map((start.stashes ?? []).map((s) => [s.id, s]));
    for (const s of end.stashes ?? []) if (!same(had.get(s.id), s)) this.stashes.add(s.id);
  }

  /** The ground still to change (nothing left: the board after the pulse, bar the units). */
  get settled(): boolean { return !this.ground.size && !this.fixtures.size && !this.stashes.size; }

  apply(batch: readonly ResolutionEntry[]): LiveChange {
    const out: LiveChange = { units: false, sight: false, ground: false };
    for (const entry of batch) {
      const i = this.entries.indexOf(entry);
      if (i > this.applied) this.applied = i;
      const e = entry.effect;
      const party = (id: number) => { const u = this.unit(id); return !!u && u.faction !== 'enemy'; };
      switch (e.kind) {
        case 'Moved': {
          const last = e.path[e.path.length - 1];
          if (last && this.move(e.unit, last)) { out.units = true; if (party(e.unit)) out.sight = true; }
          if (e.path.some((t) => this.plates.has(key(t)))) out.ground = this.take() || out.ground;
          break;
        }
        case 'Displaced':
          if (this.move(e.unit, e.to)) { out.units = true; if (party(e.unit)) out.sight = true; }
          if (this.plates.has(key(e.to))) out.ground = this.take() || out.ground;
          break;
        case 'Damaged': case 'Healed': out.units = this.patch(e.unit, { hp: e.hpAfter }) || out.units; break;
        case 'Dropped': if (this.patch(e.unit, { downed: true })) { out.units = true; if (party(e.unit)) out.sight = true; } break;
        case 'Revived': if (this.patch(e.unit, { downed: false, hp: e.hp })) { out.units = true; if (party(e.unit)) out.sight = true; } break;
        case 'ResourceGained': { const r = this.unit(e.unit)?.resource; if (r) out.units = this.patch(e.unit, { resource: { ...r, current: e.left } }) || out.units; break; }
        case 'Spawned': {
          const u = this.end?.units.find((x) => x.unitId === e.unit);
          if (u && !this.unit(e.unit)) { this.add({ ...u, tile: { ...e.tile } }); out.units = true; if (u.faction !== 'enemy') out.sight = true; }
          break;
        }
        // docs/41: a mechanism worked — the whole room moves with it (a lever's gate, a lit brazier's
        // linked ones); a wrong answer or an inscription read changes nothing but the fixture itself
        case 'Interacted':
          out.ground = (e.read !== undefined || e.beheld || e.verdict === 'Rejected' || e.verdict === 'Near' || e.verdict === 'Unknown' ? this.take([], [e.fixture]) : this.take()) || out.ground;
          break;
        case 'EventFired': case 'Other': case 'Searched':
          if (e.tiles.length) out.ground = this.take(e.tiles) || out.ground;
          break;
        // docs/60: a chute drops a hero into the cell far across the board
        case 'Fell':
          if (this.move(e.unit, e.to)) { out.units = true; out.sight = true; }
          break;
        // docs/114 MT3: the ground reacted — its patches and things exactly as the entry tells them,
        // and the squares themselves (a cloud's veil, a door burnt away) from the board after
        case 'Reacted': {
          if (e.set.length || e.things.length) {
            this.snapshot = { ...this.snapshot, board: { ...this.snapshot.board, matter: mergeMatter(this.snapshot.board.matter, e.set) }, things: mergeThings(this.snapshot.things, e.things) };
            out.ground = true;
          }
          if (e.tiles.length) out.ground = this.take(e.tiles) || out.ground;
          break;
        }
        case 'TerrainSet':
          out.ground = this.take(e.tiles) || out.ground;
          break;
        case 'Revealed': case 'TrapSprung': case 'Disarmed':
          out.ground = this.take([e.tile], [e.fixture]) || out.ground;
          break;
        // docs/61: a wonder beheld, a veil parted — the exploration's own record moves on with it
        case 'Discovered': case 'VeilParted': {
          const x = this.end?.explore;
          if (!x) break;
          const tiles = e.kind === 'VeilParted' ? x.veils[e.veil]?.tiles ?? [] : [];
          this.take(tiles, e.kind === 'Discovered' && x.wonders[e.wonder] ? [x.wonders[e.wonder]!.fixture] : []);
          this.snapshot = { ...this.snapshot, explore: x };
          out.ground = true;
          break;
        }
        // docs/140: a dungeon trap — its record (found, running, spent, the lights it snuffed) moves on
        // with it, the ground it worked comes from the board after, and a roller's thing rolls on
        case 'SnareSprung': case 'SnareActed': case 'SnareEnded': case 'SnareFound': case 'SnareDisarmed': {
          // docs/155 TR2: only this trap's record; another sprung later in the pulse shows when its own entry lands
          const c = this.end?.crawl;
          if (c && this.snapshot.crawl) {
            const now = c.snares.find((x) => x.id === e.snare);
            const rest = this.snapshot.crawl.snares.filter((x) => x.id !== e.snare);
            const snares = (now ? [...rest, now] : rest).sort((x, y) => x.id - y.id);
            this.snapshot = { ...this.snapshot, crawl: { ...this.snapshot.crawl, snares, lights: c.lights } };
          }
          if (e.kind === 'SnareActed' && e.tiles.length) this.take(e.tiles);
          if (e.kind === 'SnareActed' && (e.act === 'Roll' || e.act === 'Undone') && this.end?.things) this.snapshot = { ...this.snapshot, things: this.end.things };
          out.ground = true;
          break;
        }
        default: break;
      }
    }
    if (out.ground) out.sight = true;
    return out;
  }

  /** The fog part-way through (the scene asks the sim; marks stand as the fog it gives keeps them). */
  setFog(fog: FogView | undefined): void { if (fog) this.snapshot = { ...this.snapshot, fog }; }

  /** The party's units as the sim's fog wants them: where they stand and whether they are still up. */
  partyStands(): { unit: number; tile: Tile; down: boolean }[] {
    return this.snapshot.units.filter((u) => u.faction !== 'enemy' && u.onBoard !== false).map((u) => ({ unit: u.unitId, tile: { ...u.tile }, down: !!u.downed }));
  }

  /**
   * docs/31: a unit of the board after the pulse that is not on this one yet, and where it stands
   * now — read back from the end through the entries still to play (its first step's square, the
   * HP before its blows). Undefined while that is not known (it was called in later, say).
   */
  arrival(unitId: number): UnitState | undefined {
    const u = this.end?.units.find((x) => x.unitId === unitId);
    if (!u || u.onBoard === false || this.unit(unitId)) return undefined;
    let tile: Tile | undefined = { ...u.tile };
    let hp = u.hp;
    for (let i = this.entries.length - 1; i > this.applied && tile; i--) {
      const e = this.entries[i]!.effect;
      if (!('unit' in e) || e.unit !== unitId) continue;
      if (e.kind === 'Moved') tile = e.from ? { ...e.from } : undefined;
      else if (e.kind === 'Displaced') tile = { ...e.from };
      else if (e.kind === 'Spawned') tile = undefined;
      else if (e.kind === 'Damaged') hp = e.hpAfter + e.dealt;
      else if (e.kind === 'Healed') hp = Math.max(1, e.hpAfter - e.amount);
    }
    return tile ? { ...u, tile, hp, downed: false } : undefined;
  }

  /** The square `unitId`'s next walk steps onto first, if its next entry of its own is a walk. */
  nextStep(unitId: number): Tile | undefined {
    for (let i = this.applied + 1; i < this.entries.length; i++) {
      const e = this.entries[i]!.effect;
      if (!('unit' in e) || e.unit !== unitId) continue;
      return e.kind === 'Moved' ? e.path[0] : undefined;
    }
    return undefined;
  }

  /** Whether `unitId` still has entries to play. */
  acts(unitId: number): boolean {
    for (let i = this.applied + 1; i < this.entries.length; i++) {
      const en = this.entries[i]!;
      const e = en.effect;
      if (en.actor === unitId || ('unit' in e && e.unit === unitId)) return true;
    }
    return false;
  }

  add(u: UnitState): void {
    this.snapshot = { ...this.snapshot, units: [...this.snapshot.units, u] };
    const mind = this.end?.minds?.find((m) => m.unit === u.unitId);
    if (mind && !this.snapshot.minds?.some((m) => m.unit === u.unitId)) this.snapshot.minds = [...(this.snapshot.minds ?? []), mind];
  }

  remove(unitId: number): void {
    this.snapshot = { ...this.snapshot, units: this.snapshot.units.filter((u) => u.unitId !== unitId) };
  }

  private unit(id: number): UnitState | undefined { return this.snapshot.units.find((u) => u.unitId === id); }

  private move(id: number, to: Tile): boolean { return this.patch(id, { tile: { ...to } }); }

  private patch(id: number, p: Partial<UnitState>): boolean {
    const i = this.snapshot.units.findIndex((u) => u.unitId === id);
    if (i < 0) return false;
    const units = [...this.snapshot.units];
    units[i] = { ...units[i]!, ...p };
    this.snapshot = { ...this.snapshot, units };
    return true;
  }

  /**
   * Takes the ground from the board after the pulse: the squares in `tiles` and the fixtures and
   * stashes on them (with `ids`, those fixtures too), or with neither everything still to change but
   * the traps: a lair's trap and a trap laid on a square come with their own entries (docs/155 TR2),
   * so a door opened or a plate pressed never shows a trap sprung before its walker reaches it.
   */
  private take(tiles?: readonly Tile[], ids: readonly number[] = []): boolean {
    const end = this.end;
    if (!end || this.settled) return false;
    const w = this.snapshot.board.w;
    const at = tiles ? new Set(tiles.map(key)) : undefined;
    const trapSquare = (i: number) => !!this.snapshot.board.tiles[i]?.trap !== !!end.board.tiles[i]?.trap;
    const squares = [...this.ground].filter((i) => (at ? at.has(`${i % w},${Math.floor(i / w)}`) : !trapSquare(i)));
    const fixtures = [...this.fixtures].filter((id) => { const f = end.fixtures?.find((x) => x.id === id); return at ? ids.includes(id) || (!!f && at.has(key(f.tile))) : f?.kind !== 'Trap'; });
    const stashes = [...this.stashes].filter((id) => { const s = end.stashes?.find((x) => x.id === id); return !at || ids.includes(id) || (!!s && at.has(key(s.tile))); });
    if (!squares.length && !fixtures.length && !stashes.length) return false;
    const tilesNow = [...this.snapshot.board.tiles];
    for (const i of squares) { tilesNow[i] = end.board.tiles[i]!; this.ground.delete(i); this.changed.add(i); }
    const fx = new Map((this.snapshot.fixtures ?? []).map((f) => [f.id, f]));
    for (const id of fixtures) {
      const f = end.fixtures?.find((x) => x.id === id);
      if (f) fx.set(id, f); else fx.delete(id);
      this.fixtures.delete(id);
      if (f?.kind === 'PressurePlate') this.plates.delete(key(f.tile));
    }
    const st = new Map((this.snapshot.stashes ?? []).map((s) => [s.id, s]));
    for (const id of stashes) { const s = end.stashes?.find((x) => x.id === id); if (s) st.set(id, s); else st.delete(id); this.stashes.delete(id); }
    this.snapshot = { ...this.snapshot, board: { ...this.snapshot.board, tiles: tilesNow }, fixtures: [...fx.values()], stashes: [...st.values()] };
    return true;
  }
}
